import React from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useSeoPageStatsQuery } from '@/client/api/marketing/seo';

const num = (n: number) => n.toLocaleString();

/**
 * This listing in Google Search: impressions, clicks, average position and the
 * searches it showed up for, over Search Console's last 28 days (ff-api
 * service/seo_search_console.py). Matches the current and previous slugs, and
 * lists staging URLs apart because staging is noindex and on its way out.
 */
export const GoogleSearchPanel: React.FC<{ eid: number }> = ({ eid }) => {
  const { data, isLoading, isError } = useSeoPageStatsQuery(eid);

  if (isLoading) return <Skeleton className="h-20 w-full" />;
  if (isError || !data) return null;

  const live = data.pages.filter((p) => !p.host.startsWith('staging.'));
  const staging = data.pages.filter((p) => p.host.startsWith('staging.'));
  const queries = (live[0] ?? staging[0])?.queries ?? [];

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">Google Search</h3>
        <Link to="/admin/seo/search-console" className="text-xs text-indigo-600 hover:underline">
          All of Search Console
        </Link>
      </div>
      {data.pages.length === 0 ? (
        <p className="text-sm text-gray-400">
          Google has not shown this listing for any search in the last 28 days.
        </p>
      ) : (
        <div className="space-y-3 text-sm">
          <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              ['Impressions', data.live ? num(data.live.impressions) : '0'],
              ['Clicks', data.live ? num(data.live.clicks) : '0'],
              ['Click-through', data.live ? `${(data.live.ctr * 100).toFixed(1)}%` : '—'],
              ['Average position', data.live?.position ?? '—'],
            ].map(([label, value]) => (
              <div key={label as string} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                <dt className="text-[11px] uppercase tracking-wide text-gray-500">{label}</dt>
                <dd className="text-base font-semibold tabular-nums text-gray-900">{value}</dd>
              </div>
            ))}
          </dl>
          {staging.length > 0 && (
            <p className="text-xs text-gray-500">
              Plus {num(staging.reduce((n, p) => n + p.impressions, 0))} impressions on the staging copy, which is noindex
              and should drop out of Google over the coming weeks.
            </p>
          )}
          {queries.length > 0 && (
            <p className="text-xs leading-relaxed text-gray-600">
              <span className="font-medium">Found by:</span>{' '}
              {queries.map((q) => `${q.query} (position ${q.position})`).join(' · ')}
            </p>
          )}
          {data.window && (
            <p className="text-[11px] text-gray-400">
              {data.window.start} to {data.window.end}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
